import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';

/**
 * Thumbnail for an image endpoint that sits behind auth.
 *
 * A bare <img src> gets a 401 (no Authorization header), so the bytes are
 * fetched with axios and handed over as a blob URL. The fetch waits until
 * the element scrolls into view, which keeps a 2000-row register from
 * firing 2000 requests on mount.
 */
const LazyAuthImage = ({ src, alt = '', className = '', fallback = null, testId }) => {
  const holder = useRef(null);
  const [url, setUrl] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    setUrl(null);
    setFailed(false);
    if (!src) return undefined;
    const node = holder.current;
    if (!node) return undefined;
    let objectUrl = null;
    let cancelled = false;

    const fetchIt = async () => {
      try {
        const r = await axios.get(src, { responseType: 'blob' });
        if (cancelled) return;
        objectUrl = URL.createObjectURL(r.data);
        setUrl(objectUrl);
      } catch { if (!cancelled) setFailed(true); }
    };

    if (typeof IntersectionObserver === 'undefined') {
      fetchIt();
      return () => { cancelled = true; if (objectUrl) URL.revokeObjectURL(objectUrl); };
    }
    const obs = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) {
        obs.disconnect();
        fetchIt();
      }
    }, { rootMargin: '200px' });
    obs.observe(node);
    return () => {
      cancelled = true;
      obs.disconnect();
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [src]);

  if (!src || failed) return fallback;
  return (
    <span ref={holder} className={className} data-testid={testId}>
      {url ? (
        <img src={url} alt={alt} className="h-full w-full object-cover" />
      ) : (
        <span className="block h-full w-full animate-pulse bg-muted" />
      )}
    </span>
  );
};

export default LazyAuthImage;
